/**
 * Phase 24.4b Step 5 — Zustand store for meal-plan UI state.
 *
 * Transient UI-only state (no persistence): which day cards are
 * expanded, which recipe modal is open, whether a swap dialog is
 * active. Server data lives in TanStack Query cache; this store handles
 * the ephemeral interactive state on top of it.
 *
 * Components import `useMealPlanUi()` (the hook), NOT the underlying
 * `useMealPlanUiStore` directly. Pattern mirrors useToastStore +
 * useToast (S4a.1) and useAuthStore precedent — keeps Zustand
 * implementation detail encapsulated, allows future swap to a different
 * state library without touching consumers.
 *
 * `useMealPlanUiStore` is exported for test-reset access via
 * `useMealPlanUiStore.setState({...})` and for any future imperative
 * dispatch from non-React contexts (e.g. axios interceptor closing
 * modals on 401).
 */
import { create } from "zustand";

/**
 * Identifies a recipe-detail modal target. Carrying both `recipeId` and
 * `itemId` lets the modal show recipe content while remembering which
 * meal-plan item triggered it (so the swap button knows which item to
 * swap when the user clicks through).
 */
export interface ModalRecipeTarget {
  recipeId: number;
  itemId: number;
}

interface MealPlanUiState {
  /** ISO date strings (YYYY-MM-DD) of expanded day cards. */
  expandedDays: string[];
  /** Currently open recipe-detail modal, or null when closed. */
  modalRecipe: ModalRecipeTarget | null;
  /** Whether the swap-options dialog (nested inside the modal) is open. */
  swapDialogOpen: boolean;

  toggleDay: (date: string) => void;
  setModalRecipe: (target: ModalRecipeTarget | null) => void;
  setSwapDialogOpen: (open: boolean) => void;
  resetUi: () => void;
}

const INITIAL_STATE: Pick<
  MealPlanUiState,
  "expandedDays" | "modalRecipe" | "swapDialogOpen"
> = {
  expandedDays: [],
  modalRecipe: null,
  swapDialogOpen: false,
};

/**
 * Underlying Zustand store. Components MUST go through `useMealPlanUi()`
 * — exported here for test reset and future imperative dispatch.
 */
export const useMealPlanUiStore = create<MealPlanUiState>((set) => ({
  ...INITIAL_STATE,

  toggleDay: (date) =>
    set((s) => ({
      expandedDays: s.expandedDays.includes(date)
        ? s.expandedDays.filter((d) => d !== date)
        : [...s.expandedDays, date],
    })),

  setModalRecipe: (target) =>
    set(() =>
      // Closing the recipe modal cancels any pending swap dialog —
      // invariant: swap dialog never outlives its parent recipe modal.
      // (Locked by meal_plan_ui.test.ts edge case.)
      target === null
        ? { modalRecipe: null, swapDialogOpen: false }
        : { modalRecipe: target },
    ),

  setSwapDialogOpen: (open) => set({ swapDialogOpen: open }),

  resetUi: () => set({ ...INITIAL_STATE }),
}));

/**
 * Public hook. Components call this — never useMealPlanUiStore directly.
 * Mirrors the useToast wrap pattern for clean store-implementation
 * encapsulation.
 */
export function useMealPlanUi() {
  const expandedDays = useMealPlanUiStore((s) => s.expandedDays);
  const modalRecipe = useMealPlanUiStore((s) => s.modalRecipe);
  const swapDialogOpen = useMealPlanUiStore((s) => s.swapDialogOpen);
  const toggleDay = useMealPlanUiStore((s) => s.toggleDay);
  const setModalRecipe = useMealPlanUiStore((s) => s.setModalRecipe);
  const setSwapDialogOpen = useMealPlanUiStore((s) => s.setSwapDialogOpen);
  const resetUi = useMealPlanUiStore((s) => s.resetUi);
  return {
    expandedDays,
    modalRecipe,
    swapDialogOpen,
    toggleDay,
    setModalRecipe,
    setSwapDialogOpen,
    resetUi,
  };
}
